import React, { useState } from 'react';
import './OnboardingTour.css';

/**
 * Lightweight first-login tour. Renders a modal overlay with 3 steps that
 * introduce Symphonate's core surfaces. Dismissal is persisted to
 * localStorage so it only appears once per browser per user.
 *
 * App shows it once the user has a workspace (new signups create theirs
 * first — there's no pre-made one), so the copy points at threads in general
 * rather than a seeded sample thread that a blank workspace won't have.
 */
const STEPS = [
  {
    title: 'Welcome to Symphonate',
    body: 'Your workspace is where your team thinks together. Start a thread and Aimy, your AI teammate, joins in — chat, artifacts, decisions, and todos all live side by side.',
    cta: 'Next',
  },
  {
    title: 'AI as a teammate, not a tool',
    body: 'Aimy sees every message, captures decisions, and drafts artifacts as your conversation unfolds. Mention @AI, end a message with `?`, or try a slash command like `/doc` to get started.',
    cta: 'Next',
  },
  {
    title: 'Everything compounds',
    body: "Artifacts, decisions, and todos persist across sessions. Next time you come back you'll have a durable record of what the team agreed to — and Aimy will remember too.",
    cta: 'Get started',
  },
];

function OnboardingTour({ userId, onDismiss }) {
  const storageKey = `symphonate-onboarding-seen-${userId || 'anon'}`;
  const alreadySeen =
    typeof window !== 'undefined' && window.localStorage.getItem(storageKey) === '1';
  const [step, setStep] = useState(0);
  const [hidden, setHidden] = useState(alreadySeen);

  if (hidden) return null;

  const handleNext = () => {
    if (step + 1 >= STEPS.length) {
      handleDismiss();
    } else {
      setStep(step + 1);
    }
  };

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(storageKey, '1');
    } catch {
      // ignore storage errors
    }
    setHidden(true);
    if (onDismiss) onDismiss();
  };

  const current = STEPS[step];

  return (
    <div className="onboarding-tour-backdrop" role="dialog" aria-modal="true">
      <div className="onboarding-tour-card">
        <div className="onboarding-tour-progress">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`onboarding-tour-dot${i === step ? ' active' : ''}`}
              aria-hidden="true"
            />
          ))}
        </div>
        <h2 className="onboarding-tour-title">{current.title}</h2>
        <p className="onboarding-tour-body">{current.body}</p>
        <div className="onboarding-tour-actions">
          <button
            type="button"
            className="onboarding-tour-skip"
            onClick={handleDismiss}
            aria-label="Skip onboarding tour"
          >
            Skip
          </button>
          <button
            type="button"
            className="onboarding-tour-next"
            onClick={handleNext}
            aria-label={current.cta}
          >
            {current.cta}
          </button>
        </div>
      </div>
    </div>
  );
}

export default OnboardingTour;
